import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { num2 } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Check, Search } from "lucide-react";
import ExcelButton from "@/components/ExcelButton";
import { exportExcel, n, stamp } from "@/lib/excel";

export default function PriceBook() {
  const { can } = useAuth();
  const editable = can("vouchers");
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({});
  const [q, setQ] = useState("");
  const load = () => api.get("/products").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const ql = q.trim().toLowerCase();
  const list = items.filter(p => !ql || [p.name, p.unit].some(v => String(v || "").toLowerCase().includes(ql)));

  const save = async (p) => {
    const v = edits[p.id];
    if (v === undefined || Number(v) === Number(p.rate)) return;
    if (v === "" || Number(v) < 0) return toast.error("Enter a valid rate");
    try {
      await api.put(`/products/${p.id}`, { name: p.name, unit: p.unit, rate: Number(v), enable_multiplier_breakdown: p.enable_multiplier_breakdown });
      toast.success(`${p.name} · ₹${num2(v)}`);
      setEdits(e => { const n = { ...e }; delete n[p.id]; return n; }); load();
    } catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
  };
  const focusNext = (i) => setTimeout(() => document.querySelector(`[data-testid='pricebook-rate-${list[i + 1]?.id}']`)?.select(), 30);

  return (
    <div className="space-y-3" data-testid="price-book">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="text-xs text-slate-500">Org-level rates used on invoices when a customer has no custom price. Edit inline · Enter saves and moves down.</div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
        <ExcelButton testid="pricebook-excel-btn" onClick={() => exportExcel(`PriceBook-${stamp()}`, [{ name: "Price Book", title: "Organization Price Book", columns: [{ header: "Product", get: p => p.name, width: 30 }, { header: "Unit", get: p => p.unit }, { header: "Rate", get: p => n(p.rate), num: true }, { header: "Status", get: p => p.active === false ? "Inactive" : "Active" }], rows: list }])}/>
        <div className="flex items-center h-9 border border-slate-300 rounded-md bg-white focus-within:border-blue-600 flex-1 sm:w-64">
          <Search size={14} className="ml-2 text-slate-400 shrink-0"/>
          <input data-testid="pricebook-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search product or unit…" className="flex-1 h-full px-2 text-sm outline-none bg-transparent"/>
        </div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md table-scroll">
        <table className="acct-table fit" data-testid="pricebook-table">
          <thead><tr><th>Product</th><th style={{width:"100px"}}>Unit</th><th style={{width:"160px"}} className="num">Org Rate ₹</th><th style={{width:"60px"}}></th></tr></thead>
          <tbody>
            {list.map((p, i) => {
              const val = edits[p.id] ?? p.rate;
              const dirty = edits[p.id] !== undefined && Number(edits[p.id]) !== Number(p.rate);
              return (
                <tr key={p.id} data-testid={`pricebook-row-${p.id}`}>
                  <td className="font-medium">{p.name}</td>
                  <td className="mono text-slate-500">{p.unit}</td>
                  <td><input data-testid={`pricebook-rate-${p.id}`} className={`cell-input num ${dirty ? "bg-amber-50" : ""}`} type="number" step="0.01" value={val} readOnly={!editable}
                    onFocus={(e)=>e.target.select()} onChange={(e)=>setEdits(s=>({...s,[p.id]:e.target.value}))} onBlur={()=>save(p)}
                    onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); save(p); focusNext(i); } }}/></td>
                  <td className="text-right">{dirty && <button data-testid={`pricebook-save-${p.id}`} onMouseDown={(e)=>e.preventDefault()} onClick={()=>save(p)} className="p-1 text-emerald-700 hover:text-emerald-900" title="Save"><Check size={14}/></button>}</td>
                </tr>
              );
            })}
            {!list.length && <tr><td colSpan="4" className="text-center py-8 text-slate-400">{items.length ? "No products match your search." : "No products yet."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
